import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Page,
  Banner,
  Button as PolarisButton,
  Modal,
  Text,
  Spinner,
} from "@shopify/polaris";
import { btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import useIsPhone from "features/files/use-is-phone";
import {
  startTrainingAttempt,
  submitTrainingAttempt,
  TrainingQuestion,
} from "services/work-security-training.service";
import {
  ExamHeader,
  ExamLegend,
  ExamOptionButton,
  ExamQuestionGrid,
  ExamQuestionStatus,
  ExamTimer,
  ExamTimerCompact,
  optionLetter,
} from "./training-exam-ui";

export default function TrainingTakeTestContainer() {
  const { companyId, assignmentId } = useParams<{
    companyId: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [error, setError] = useState("");
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [now, setNow] = useState(Date.now());
  const [testName, setTestName] = useState("");
  const [passPercent, setPassPercent] = useState(80);
  const [questions, setQuestions] = useState<
    Array<Omit<TrainingQuestion, "correctOptionIds"> & { multiSelect?: boolean }>
  >([]);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [visited, setVisited] = useState<Record<string, boolean>>({});
  const [reviewMarks, setReviewMarks] = useState<Record<string, boolean>>({});
  const [index, setIndex] = useState(0);
  const [result, setResult] = useState<{
    passed: boolean;
    scorePercent: number;
    correct: number;
    total: number;
  } | null>(null);

  const remainingMs = Math.max(0, expiresAt - now);
  const urgent = remainingMs > 0 && remainingMs < 60_000;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const begin = useCallback(async () => {
    if (!companyId || !assignmentId) return;
    setLoading(true);
    setError("");
    try {
      const data = await startTrainingAttempt(companyId, assignmentId);
      setAttemptId(data.attempt.id);
      setExpiresAt(data.attempt.expiresAt);
      setTestName(data.test.name);
      setPassPercent(data.test.passPercent);
      const qs = data.questions || [];
      setQuestions(qs);
      setIndex(0);
      setAnswers({});
      setReviewMarks({});
      const firstId = qs[0]?.id;
      setVisited(firstId ? { [firstId]: true } : {});
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("start_failed", { defaultValue: "Could not start the test" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, assignmentId, t]);

  useEffect(() => {
    begin();
  }, [begin]);

  const goTo = useCallback(
    (nextIndex: number) => {
      const q = questions[nextIndex];
      if (!q?.id) return;
      setIndex(nextIndex);
      setVisited((prev) => ({ ...prev, [q.id!]: true }));
    },
    [questions]
  );

  const doSubmit = useCallback(async () => {
    if (!companyId || !attemptId || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = questions.map((q) => ({
        questionId: q.id!,
        selectedOptionIds: answers[q.id!] || [],
      }));
      const data = await submitTrainingAttempt(companyId, attemptId, payload);
      setResult({
        passed: data.passed,
        scorePercent: data.scorePercent,
        correct: data.correct,
        total: data.total,
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("submit_failed", { defaultValue: "Could not submit the test" })
      );
    } finally {
      setSubmitting(false);
    }
  }, [companyId, attemptId, submitting, questions, answers, t]);

  useEffect(() => {
    if (!attemptId || result || loading) return;
    if (remainingMs <= 0) {
      doSubmit();
    }
  }, [remainingMs, attemptId, result, loading, doSubmit]);

  const current = questions[index];

  const getStatus = useCallback(
    (i: number): ExamQuestionStatus => {
      const q = questions[i];
      if (!q?.id) return "not_attempted";
      if (reviewMarks[q.id]) return "review";
      const selected = answers[q.id] || [];
      if (selected.length > 0) return "answered";
      if (visited[q.id]) return "not_answered";
      return "not_attempted";
    },
    [questions, answers, visited, reviewMarks]
  );

  const toggleAnswer = (questionId: string, optionId: string, multi: boolean) => {
    setAnswers((prev) => {
      const currentIds = prev[questionId] || [];
      if (!multi) {
        return { ...prev, [questionId]: [optionId] };
      }
      const next = currentIds.includes(optionId)
        ? currentIds.filter((id) => id !== optionId)
        : [...currentIds, optionId];
      return { ...prev, [questionId]: next };
    });
  };

  const toggleReview = () => {
    if (!current?.id) return;
    setReviewMarks((prev) => ({
      ...prev,
      [current.id!]: !prev[current.id!],
    }));
  };

  const answeredCount = useMemo(
    () => Object.values(answers).filter((ids) => ids.length > 0).length,
    [answers]
  );
  const unansweredCount = questions.length - answeredCount;

  const cancelLabel = t("cancel", { defaultValue: "Cancel" });
  const submitLabel = t("submit_test", { defaultValue: "Submit test" });
  const cancelSubmit = () => setConfirmingSubmit(false);
  const confirmSubmit = () => {
    setConfirmingSubmit(false);
    doSubmit();
  };

  if (loading) {
    return (
      <Page fullWidth title={t("training_test", { defaultValue: "Training test" })}>
        <div className="flex justify-center py-24">
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (result) {
    return (
      <Page
        fullWidth
        title={testName}
        backAction={{
          onAction: () =>
            navigate(
              `/app/${companyId}/my-work/training/${assignmentId}?step=test`
            ),
        }}
      >
        <div className="mx-auto max-w-3xl space-y-4 pb-10">
          <div
            className={`rounded-3xl p-8 text-center ${
              result.passed
                ? "bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-50"
                : "bg-rose-50 text-rose-950 dark:bg-rose-950/40 dark:text-rose-50"
            }`}
          >
            <Text as="h2" variant="headingXl">
              {result.passed
                ? t("test_passed", { defaultValue: "Passed" })
                : t("test_failed", { defaultValue: "Failed" })}
            </Text>
            <p className="mt-2 text-lg opacity-90">
              {t("score_summary", {
                defaultValue:
                  "{{score}}% · {{correct}}/{{total}} correct · pass mark {{pass}}%",
                score: result.scorePercent,
                correct: result.correct,
                total: result.total,
                pass: passPercent,
              })}
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <PolarisButton
                onClick={() =>
                  navigate(
                    `/app/${companyId}/my-work/training/${assignmentId}?step=test`
                  )
                }
              >
                {t("back_to_training", { defaultValue: "Back to training" })}
              </PolarisButton>
              {!result.passed && (
                <PolarisButton
                  variant="primary"
                  onClick={() => {
                    setResult(null);
                    begin();
                  }}
                >
                  {t("reattempt", { defaultValue: "Reattempt" })}
                </PolarisButton>
              )}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title={testName || t("training_test", { defaultValue: "Training test" })}
      backAction={{
        onAction: () =>
          navigate(
            `/app/${companyId}/my-work/training/${assignmentId}?step=test`
          ),
      }}
    >
      <div className="pb-8">
        {/* Below lg the side column (with the big timer) sits under the question, so the time would be out of sight while answering. */}
        <div className="sticky top-0 z-10 mb-3 lg:hidden">
          <ExamTimerCompact remainingMs={remainingMs} urgent={urgent} />
        </div>

        <Modal
          open={confirmingSubmit}
          onClose={() => setConfirmingSubmit(false)}
          title={t("submit_test", { defaultValue: "Submit test" })}
          primaryAction={isPhone ? undefined : { content: submitLabel, onAction: confirmSubmit }}
          secondaryActions={isPhone ? undefined : [{ content: cancelLabel, onAction: cancelSubmit }]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              {unansweredCount > 0
                ? t("training_submit_unanswered", {
                    defaultValue:
                      "{{count}} question(s) unanswered. Submit the test anyway?",
                    count: unansweredCount,
                  })
                : t("training_submit_all_answered", {
                    defaultValue: "All questions are answered. Submit the test?",
                  })}
            </Text>
            {/* On a phone the two buttons are drawn here: Polaris's footer puts them 8 px apart. */}
            {isPhone && (
              <div data-testid="submit-confirm-buttons" className="mt-4 flex gap-3 [&>button]:flex-1">
                <button type="button" className={btnSecondaryCls} onClick={cancelSubmit}>
                  {cancelLabel}
                </button>
                <button type="button" className={btnPrimaryCls} onClick={confirmSubmit}>
                  {submitLabel}
                </button>
              </div>
            )}
          </Modal.Section>
        </Modal>

        {error && (
          <div className="mb-3">
            <Banner tone="critical" onDismiss={() => setError("")}>
              {error}
            </Banner>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-950">
          <ExamHeader
            title={
              testName ||
              t("online_test", { defaultValue: "Online Test" })
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
            <div className="flex min-h-[520px] flex-col border-zinc-200 dark:border-zinc-700 lg:border-r">
              {current ? (
                <>
                  <div className="flex-1 space-y-4 p-5 sm:p-6">
                    <div>
                      <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                        {t("question_n", {
                          defaultValue: "Question {{n}}",
                          n: index + 1,
                        })}
                      </p>
                      <p className="mt-3 text-base leading-relaxed text-zinc-800 dark:text-zinc-100">
                        {current.questionText}
                      </p>
                      <p className="mt-2 text-xs text-zinc-500">
                        {current.multiSelect
                          ? t("select_all_that_apply", {
                              defaultValue: "Select all that apply",
                            })
                          : t("select_one_answer", {
                              defaultValue: "Select one answer",
                            })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {(current.options || []).map((opt, oi) => {
                        const selected = (answers[current.id!] || []).includes(
                          opt.id
                        );
                        return (
                          <ExamOptionButton
                            key={opt.id}
                            letter={optionLetter(oi)}
                            text={opt.text}
                            selected={selected}
                            multiSelect={Boolean(current.multiSelect)}
                            onClick={() =>
                              toggleAnswer(
                                current.id!,
                                opt.id,
                                Boolean(current.multiSelect)
                              )
                            }
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Phone: Previous/Next side by side on top, then "mark for review", and "Submit" alone at the bottom, apart from the navigation. */}
                  <div className="flex flex-col gap-3 border-t border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-2">
                    <button
                      type="button"
                      onClick={toggleReview}
                      className={`rounded-md px-4 py-2 text-sm font-semibold text-white max-md:order-2 max-md:min-h-11 ${
                        reviewMarks[current.id!]
                          ? "bg-zinc-700"
                          : "bg-rose-600 hover:bg-rose-700"
                      }`}
                    >
                      {reviewMarks[current.id!]
                        ? t("unmark_review", {
                            defaultValue: "Unmark review",
                          })
                        : t("mark_for_review", {
                            defaultValue: "Mark for review",
                          })}
                    </button>

                    <div className="grid grid-cols-2 gap-2 max-md:order-1 md:flex">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={() => goTo(index - 1)}
                        className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 hover:bg-sky-700 max-md:min-h-11"
                      >
                        {t("previous", { defaultValue: "Previous" })}
                      </button>
                      <button
                        type="button"
                        disabled={index >= questions.length - 1}
                        onClick={() => goTo(index + 1)}
                        className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 hover:bg-sky-700 max-md:min-h-11"
                      >
                        {t("next", { defaultValue: "Next" })}
                      </button>
                    </div>

                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => setConfirmingSubmit(true)}
                      className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 max-md:order-3 max-md:mt-3 max-md:min-h-11"
                    >
                      {submitting
                        ? t("submitting", { defaultValue: "Submitting…" })
                        : t("submit_test", { defaultValue: "Submit Test" })}
                    </button>
                  </div>
                </>
              ) : (
                <div className="p-6">
                  <Banner tone="warning">
                    {t("no_questions", {
                      defaultValue: "This test has no questions.",
                    })}
                  </Banner>
                </div>
              )}
            </div>

            <aside className="space-y-3 bg-zinc-50 p-3 dark:bg-zinc-900/50">
              <div className="max-lg:hidden">
                <ExamTimer remainingMs={remainingMs} urgent={urgent} />
              </div>
              <ExamQuestionGrid
                count={questions.length}
                currentIndex={index}
                getStatus={getStatus}
                onSelect={goTo}
              />
              <p className="px-1 text-xs text-zinc-500">
                {t("question_progress", {
                  defaultValue:
                    "Question {{current}} of {{total}} · {{answered}} answered",
                  current: questions.length ? index + 1 : 0,
                  total: questions.length,
                  answered: answeredCount,
                })}
              </p>
            </aside>
          </div>

          <div className="border-t border-zinc-200 p-3 dark:border-zinc-700">
            <ExamLegend />
          </div>
        </div>
      </div>
    </Page>
  );
}
